import { useQuery } from '@apollo/client';
import { DateFormat, DateService } from 'frontend-toolkit';
import * as React from 'react';
import { Link } from 'react-router-dom';

import * as FormatRecipients from 'core/formatRecipients';
import FilteredEmailsQuery from 'core/gql/queries/filtered-emails';

import PaginatedTable from 'components/common/table/paginated-table';

interface Props {
  orgId: number;
}

export interface MessageType {
  id: string;
  subject: string;
  body: string;
  isDraft: boolean;
  emailRecipients: [EmailRecipient];
  senderName: string;
  sentAt: string;
}

export interface EmailRecipient {
  divisionId: number;
  divisionName: string;
  seasonId: number;
  seasonName: string;
  programId: number;
  programName: string;
}

const emptyFunc = () => {};

const MessageList: React.FC<Props> = ({ orgId }: Props) => {
  const [currentPage, setCurrentPage] = React.useState(1);

  const { data, loading } = useQuery(FilteredEmailsQuery, {
    variables: { organizationId: orgId, pageNumber: currentPage, pageSize: 50 },
  });

  const maxNumberProgramToDisplay = 3;

  const prepRecipientsList = (emailRecipients: EmailRecipient[]) => {
    const sortedProgramSeasonDivisions =
      FormatRecipients.getSortedDivisionsGroupedByProgramsSeasons(
        emailRecipients
      );
    // show only the first three program season divisions
    const selectedPrograms = Object.entries(sortedProgramSeasonDivisions).slice(
      0,
      maxNumberProgramToDisplay
    );
    const numberOfSortedProgramSeasonDivisions = Object.keys(
      sortedProgramSeasonDivisions
    ).length;

    type Program = [programSeasonName: string, divisions: []];

    return selectedPrograms.map(
      ([programSeasonName, divisions]: Program, index) => {
        const divisionNames: string = divisions.join(', ');
        const shouldDisplayEllipsisAtEnd =
          numberOfSortedProgramSeasonDivisions > maxNumberProgramToDisplay &&
          index === maxNumberProgramToDisplay - 1;
        return (
          <div key={divisionNames} className="u-textEllipsis u-textNoWrap">
            {programSeasonName}&nbsp;(
            <span className="u-colorGrey">{divisionNames}</span>)
            {shouldDisplayEllipsisAtEnd ? '...' : ''}
          </div>
        );
      }
    );
  };

  const messages =
    data?.filteredEmails.data
      .filter((row: MessageType) => !row.isDraft)
      .map((item) => ({
        from: item.senderName,
        recipients: prepRecipientsList(item.emailRecipients),
        date: DateService.dateToString(
          new Date(item.sentAt),
          DateFormat.MM_DD_YYYY
        ),
        ...item,
      })) || [];

  const columns = [
    {
      label: 'Subject',
      name: 'subject',
      align: 'left',
      mods: 'u-spaceLeftLg u-flexShrink1',
      render: (col, row) => {
        const { id, subject, isDraft } = row;
        const actionLink = isDraft ? 'edit' : 'sent';

        return (
          <Link to={`/organizations/${orgId}/messages/${actionLink}/${id}`}>
            {subject}
          </Link>
        );
      },
    },
    {
      label: 'From',
      name: 'from',
      align: 'left',
      mods: 'u-flexShrink1',
    },
    {
      label: 'Recipients',
      name: 'recipients',
      align: 'left',
      mods: 'u-flexGrow1',
      style: { width: '100px' },
    },
    {
      label: 'Date',
      name: 'date',
      align: 'left',
      mods: 'u-flexShrink1',
    },
  ];

  return (
    <PaginatedTable
      rows={messages}
      pagination={{
        totalItems: data?.filteredEmails.metadata.totalEntries || 10,
        currentPage,
        setCurrentPage,
      }}
      isLoading={loading}
      columns={columns}
      sorting={{
        sortBy: '',
        setSortBy: emptyFunc,
        sortAscending: false,
        setSortAscending: emptyFunc,
      }}
    />
  );
};

export default MessageList;
